import { TILE } from '../map/world';
import { toast } from '../dom';
import { startCoach, type CoachHandle, type CoachStep } from './coach';
import { markTutorialDone, tutorialStep, setTutorialStep } from './state';
import { isFelled, isGathered, isMined, type FarmCtx } from '../views/farm/context';
import type { Decor } from '../map/world';

/**
 * The guided tour of the real farm map (A4). It runs once, on a player's first
 * visit, teaching the core loop hands-on in the CORRECT order the game enforces:
 * move → till → WATER → plant → chop → mine → build a station → sell.
 *
 * Each step points a bouncing arrow (and, for UI controls, a highlight ring) at
 * the ACTUAL thing to click — the real Hoe slot, the real bed, the nearest tree,
 * the Build button — and auto-advances when that action completes. Fully
 * skippable; wrapped so a bug can never wedge the farm.
 */
export function startFarmTour(c: FarmCtx, host: HTMLElement): CoachHandle | null {
  try {
    const worldToScreen = (tx: number, ty: number) => {
      const cam = c.cam;
      if (!cam) return null;
      const sc = cam.getScale();
      return { x: (tx + 0.5) * TILE * sc + cam.world.x, y: (ty + 0.5) * TILE * sc + cam.world.y };
    };

    const heroTile = () => (c.player ? { tx: Math.floor(c.player.x / TILE), ty: Math.floor(c.player.y / TILE) } : null);
    const dom = (sel: string) => () => document.querySelector(sel);

    // The player's own bed in a given state, NEAREST to the hero (there may be
    // several beds — always point at the one they're standing by).
    const bed = (pred: (p: { state: string; watered: boolean; cropId: string | null }) => boolean) => () => {
      const h = heroTile();
      const beds = (c.farm?.plots ?? []).filter(pred as never) as { tx: number; ty: number }[];
      if (!beds.length) return null;
      if (!h) return { tx: beds[0].tx, ty: beds[0].ty };
      let best = beds[0], bd = Infinity;
      for (const p of beds) { const d = (p.tx - h.tx) ** 2 + (p.ty - h.ty) ** 2; if (d < bd) { bd = d; best = p; } }
      return { tx: best.tx, ty: best.ty };
    };
    const anyBed = bed(() => true);
    const tilledDry = bed((p) => p.state === 'TILLED' && !p.watered);
    const wateredBed = bed((p) => p.watered && !p.cropId);

    /**
     * Nearest node to the hero that can still SATISFY the step (what the arrow aims at).
     *
     * Two rules, both learned from arrows that pointed at dead ends:
     *
     *  - Only the kinds listed. The mining step used to aim at
     *    `['ore_rock', 'rock']`, and those are not the same thing: `ore_rock` is
     *    an ore vein, mined with the pickaxe, and `rock` is loose ground stone
     *    picked up by hand (GATHER_KINDS). Plain rocks are scattered everywhere,
     *    so the nearest one was almost always a `rock` — the arrow pointed at a
     *    pebble that can never fire `mine.break`, and the step could not be
     *    completed by doing exactly what it pointed at. Owner: "not all stones
     *    considered as a stone".
     *
     *  - Skip what has already been spent. A felled tree or a worked-out vein is
     *    still in `world.decor` (it is the same node, drawn as a stump or hidden
     *    until it regrows), so without this the arrow could park on something the
     *    player had just used up and the step would strand in the same way.
     */
    const nearestDecor = (kinds: string[]) => () => {
      const h = heroTile(); const w = c.world;
      if (!h || !w) return null;
      let best: { tx: number; ty: number } | null = null, bd = Infinity;
      for (const d of w.decor as Decor[]) {
        if (!kinds.includes(d.kind)) continue;
        if (isMined(c, d) || isFelled(c, d) || isGathered(c, d)) continue;
        const dd = (d.tx - h.tx) ** 2 + (d.ty - h.ty) ** 2;
        if (dd < bd) { bd = dd; best = { tx: d.tx, ty: d.ty }; }
      }
      return best;
    };

    /**
     * "…and it is 34 tiles that way." Shown by the coach only when the target is
     * off screen, which on a 683-tile map is the normal case for the nearest tree
     * or vein. Without it the arrow was simply invisible and the step read as
     * broken — owner: "tutorial should show where's the nearest ore is, to mine it."
     */
    const distanceTo = (name: string, at: () => { tx: number; ty: number } | null) => () => {
      const h = heroTile(); const t = at();
      if (!h || !t) return null;
      const d = Math.round(Math.hypot(t.tx - h.tx, t.ty - h.ty));
      const dx = t.tx - h.tx, dy = t.ty - h.ty;
      // Eight-point compass from the vector — plainer to act on than a bearing.
      const dirs = ['E', 'SE', 'S', 'SW', 'W', 'NW', 'N', 'NE'];
      const oct = dirs[(Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) + 8) % 8];
      return `${name} · ${d} tiles ${oct}`;
    };

    /**
     * Hide the arrow the moment the step's node has been consumed.
     *
     * `nearestDecor` already refuses to AIM at a felled tree or a worked-out vein,
     * so the arrow hops to the next one — but the owner's report is about the beat
     * right after the swing, where a marker still hangs over what you just cut:
     * "arrow and axe on the tree leg after tree cut."
     *
     * The baseline is taken in `onEnter` rather than testing "has anything ever
     * been felled", because on the real farm the answer is usually yes — a player
     * reaching this step has chopped before, and the server hydrates those
     * harvests on load. Comparing against the count AT STEP ENTRY is the only
     * version that means "you did the thing I just asked for".
     */
    let felledAtEntry = 0;
    let stumpsAtEntry = 0;
    let minedAtEntry = 0;
    // BOTH clauses must be deltas. `|| clearedStumps.size > 0` was absolute, and
    // clearedStumps is hydrated from the server — so on any farm where the player
    // had ever grubbed a stump the chop arrow was hidden the instant the step
    // opened, and the step read as broken. That is the same class of mistake the
    // baseline exists to avoid; it slipped into the second clause.
    const didFell = () => (c.felledUntil?.size ?? 0) > felledAtEntry
      || (c.clearedStumps?.size ?? 0) > stumpsAtEntry;
    const didMine = () => (c.minedUntil?.size ?? 0) > minedAtEntry;
    /** The step's target, until the player has done it — then nothing to point at. */
    const untilDone = (done: () => boolean, at: () => { tx: number; ty: number } | null) =>
      () => (done() ? null : at());

    let moveFrom = '';
    const heroKey = () => { const t = heroTile(); return t ? `${t.tx},${t.ty}` : ''; };

    const steps: CoachStep[] = [
      { title: 'Welcome to Terraboma', manualOk: true,
        text: 'This is your farm. Grow crops, gather wood & ore, craft gear, earn Coins. I\'ll point at exactly what to click.' },

      { title: 'Move', text: 'Walk with W A S D / arrow keys, or just click a spot on the ground to walk there.',
        onEnter: () => { moveFrom = heroKey(); },
        until: () => !!moveFrom && heroKey() !== moveFrom },

      { title: 'Pick the Hoe', text: 'Click the Hoe in your toolbar to select it (it lights up gold).',
        domAt: dom('[data-tut="tool-hoe"]'),
        until: () => c.selectedTool === 'hoe' },

      { title: 'Till the soil', text: 'With the Hoe selected, click a patch of grass to dig a plot bed.',
        onActionType: 'field.till' },

      { title: 'Water the bed', text: 'A fresh bed is dry. Click the bed you just dug to water it — crops won\'t grow dry.',
        arrowAt: () => tilledDry() ?? anyBed(),
        onActionType: 'field.water' },

      { title: 'Plant a seed', text: 'Now click the watered bed again and choose a crop — Wheat is a cheap first crop.',
        arrowAt: () => wateredBed() ?? anyBed(),
        onActionType: 'field.plant' },

      { title: 'Chop a tree', text: 'Pick the Axe, then click a tree to chop it for Wood.',
        domAt: dom('[data-tut="tool-axe"]'),
        onEnter: () => {
          felledAtEntry = c.felledUntil?.size ?? 0;
          stumpsAtEntry = c.clearedStumps?.size ?? 0;
        },
        arrowAt: untilDone(didFell, nearestDecor(['tree'])),
        arrowLabel: distanceTo('Tree', nearestDecor(['tree'])),
        onActionType: 'wood.chop' },

      // "a rock" was the wrong noun: most rocks on the map are loose stone you pick
      // up by hand, and only an ORE VEIN can be mined. The arrow now aims at a vein
      // and the words name what to look for, so the two agree.
      { title: 'Mine some ore', text: 'Pick the Pickaxe, then click an ore vein — a boulder with coloured ore showing in it. Follow the arrow if it is not on screen.',
        domAt: dom('[data-tut="tool-pickaxe"]'),
        onEnter: () => { minedAtEntry = c.minedUntil?.size ?? 0; },
        arrowAt: untilDone(didMine, nearestDecor(['ore_rock'])),
        arrowLabel: distanceTo('Ore vein', nearestDecor(['ore_rock'])),
        onActionType: 'mine.break' },

      // Your OWN house comes before the villagers' — it is where you sleep and
      // where you respawn, and without one "out of energy" means a 4-hour wait.
      // It sits here because Chop + Mine have just paid for it (8 wood, 4 stone).
      { title: 'Build your Hut', text: 'Open Build and place a Hut — your home. Sleep there any time for a full energy bar, and you respawn beside it.',
        domAt: dom('[data-tut="build"]'),
        // NOT onActionType: the next step keys on 'field.build' too, so building a
        // torch here would tick both off. The hut row itself is the condition.
        until: () => (c.farm?.buildings ?? []).some((b) => b.kind === 'hut') },

      { title: 'Build a station', text: 'Open Build and place a Crafting Table. Then walk up and click it to craft — real stations, no menus.',
        domAt: dom('[data-tut="build"]'),
        onActionType: 'field.build' },

      { title: 'Sell for Coins', manualOk: true,
        // The Market tab is gone: trading lives at a Forager Hut you build, so this
        // step points at Build rather than a footer button that no longer exists.
        text: 'Build a Forager Hut, then click it: the keeper buys your produce, loot and ore for Coins — and sells you wood and stone when a build comes up short.',
        domAt: dom('[data-tut="build"]') },

      { title: 'Shop & Arena', manualOk: true,
        text: 'Up top: the Shop spends Coins on upgrades; the Arena is a for-Coins PvP battle royale. Quests & Awards track your goals.' },

      { title: "You're set!", manualOk: true,
        text: 'That\'s the loop: farm & gather → craft → sell & fight. Replay this any time from ⚙️ Settings. Have fun! 🌱' },
    ];

    return startCoach({
      host, steps, worldToScreen, chapter: 'Tutorial',
      // Resume silently where the reload interrupted us. The chop/mine steps
      // re-baseline in onEnter, so a resumed player is asked for the ONE tree the
      // step is about, not for a delta against a farm the server already hydrated.
      startAt: tutorialStep(), onStep: setTutorialStep,
      onDone: (skipped) => {
        markTutorialDone();
        toast(skipped ? 'Tutorial skipped — replay it in Settings' : 'Tutorial complete — happy farming! 🌱');
      },
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[tutorial] failed to start:', e);
    return null;
  }
}
